import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useVerifyWalletTokenMutation } from './walletCardApiService'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import {
  useCreateSurgeryPass,
  useGetSurgeryPass,
} from 'lib/services/api/wallet-service'
import { LegalOverlayState, PatientFormData } from './PatientWalletCard.types'

// Legal content for overlays
export const LEGAL_CONTENT = {
  hipaaConsent: {
    title: 'HIPAA Authorization',
    content: `
      <p>I authorize the release of patient medical records related to medical devices that have been surgically implanted, including the name of the manufacturer, the name of device, and/or the medical device serial number, electronically via web-based portal.</p>
      
      <p>The purpose of this authorization is to me to access information on the medical device that was implanted in me (e.g., manufacturer, name of device and/or serial number), so that such information can be accessed in an E-Wallet that is accessible to me or my authorized representative.</p>
      
      <p>In accordance with applicable state law and the Health Insurance Portability and Accountability Act of 1996 (HIPAA) Privacy Standards, I, as the patient or patient's authorized representative, understand and agree that:</p>
      
      <p>This authorization does not include information relating to alcohol and drug abuse, mental health treatment, psychotherapy notes) and confidential HIV related information. In the event the health information described above includes any of these types of information, and I check the applicable box, I specifically authorize the release of such information to the recipient identified above.</p>
      
      <p>I have the right to revoke this authorization, in writing, at any time, except where action has been taken based upon my authorization. To revoke this authorization, I must do so in writing and send it to the appropriate disclosing party.</p>
      
      <p>It is possible that information used or disclosed with my permission may be re-disclosed by the recipient and is no longer protected by the HIPAA Privacy Standards or state law.</p>
      
      <p>Signing this authorization is voluntary. My treatment by any party, payment, enrollment in a health plan, or eligibility for benefits may not be conditioned upon my signing of this authorization.</p>
      
      <p>By checking the box below, I, as the patient or the authorized representative of the patient, intend to electronically sign this HIPAA Authorization for Release of Protected Health Information, which authorizes my healthcare provider to disclose my medical device information to MedGEO, and authorizes MedGEO to apply my electronic signature to this HIPAA Authorization for Release of Protected Health Information.</p>
      
      <p>I will receive an electronic copy of this authorization after I have signed it. An electronic copy of this authorization is as valid as the original.</p>
    `,
  },
}

export const usePatientWalletCard = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const { iservicesClient } = useApolloClients()

  // State for legal content overlay
  const [legalOverlay, setLegalOverlay] = useState<LegalOverlayState>({
    open: false,
    content: {
      title: '',
      content: '',
    },
  })

  const {
    verifyToken: verifyTokenMutation,
    loading: verifyLoading,
    error: verifyError,
    data: verifyData,
  } = useVerifyWalletTokenMutation(iservicesClient)

  const {
    mutateAsync: createSurgeryPass,
    error: createSurgeryPassError,
    isLoading: createSurgeryPassLoading,
    isSuccess: surgeryPassCreated,
  } = useCreateSurgeryPass()

  // Check if a surgery pass already exists
  const { data: existingSurgeryPass } = useGetSurgeryPass(
    verifyData?.data?.patientId || '',
    verifyData?.data?.surgeryId || '',
    { enabled: !!verifyData?.data?.patientId && !!verifyData?.data?.surgeryId }
  )

  // Function to open legal content overlay
  const handleOpenLegalOverlay =
    (contentType: 'hipaaConsent') => (event: React.MouseEvent) => {
      event.preventDefault()
      setLegalOverlay({
        open: true,
        content: LEGAL_CONTENT[contentType],
      })
    }

  // Function to close legal content overlay
  const handleCloseLegalOverlay = () => {
    setLegalOverlay({
      ...legalOverlay,
      open: false,
    })
  }

  useEffect(() => {
    verifyTokenMutation({
      variables: { validateWalletInvitationInput: { token } },
    })
  }, [token, verifyTokenMutation])

  const handleSubmit = async (formData: PatientFormData) => {
    createSurgeryPass({
      patientId: verifyData?.data?.patientId!,
      surgeryId: verifyData?.data?.surgeryId!,
    })
  }

  // Determine if we should show the download view
  const showDownloadView = surgeryPassCreated || !!existingSurgeryPass

  return {
    verifyLoading,
    verifyError,
    verifyData,
    showDownloadView,
    legalOverlay,
    createSurgeryPassLoading,
    createSurgeryPassError,
    handleOpenLegalOverlay,
    handleCloseLegalOverlay,
    handleSubmit,
  }
}
